/**
 * Diagnostic d'autocorrélation, affiché sous la carte.
 *
 * L'écran d'origine montrait quarante-trois caractéristiques toutes rouges, « 100 % hors
 * contrôle ». Le calcul était juste et le résultat inexploitable — un écran entièrement
 * rouge n'apprend rien, et pire : il apprend à ne plus regarder.
 *
 * Ce panneau explique pourquoi, et surtout **ce qu'il faut faire**. Deux règles :
 *
 * 1. **Rien n'est corrigé en silence.** Les limites affichées restent celles du calcul
 *    standard. Les ajuster sans le dire donnerait un chiffre de plus auquel personne ne
 *    comprend rien ; nommer le phénomène donne un argument utilisable en réunion.
 * 2. **Le remède est un intervalle concret.** « Une mesure sur 49 » se met en œuvre ;
 *    « votre série est autocorrélée » s'oublie.
 */

import { Activity, AlertTriangle, CheckCircle2 } from "lucide-react";

import { NATURE_LABELS, type SeriesDiagnosis } from "./mappingApi";
import { Select, cn } from "./ui/primitives";

export function DiagnosisPanel({
  diagnosis,
  code,
  onNatureChange,
}: {
  diagnosis: SeriesDiagnosis;
  code: string;
  onNatureChange: (code: string, nature: string) => void;
}) {
  const calm = diagnosis.severity === "none";

  const tone = calm
    ? { border: "border-good/30", bg: "bg-good-soft", text: "text-good" }
    : diagnosis.severity === "strong"
      ? { border: "border-warn/40", bg: "bg-warn-soft", text: "text-warn" }
      : { border: "border-line", bg: "bg-surface-2", text: "text-muted" };

  return (
    <div className={cn("border-t px-4 py-3", tone.border, tone.bg)}>
      <div className="flex flex-wrap items-start gap-2">
        <span className={cn("mt-0.5 shrink-0", tone.text)}>
          {calm ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : diagnosis.is_misleading ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <Activity className="h-4 w-4" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className={cn("text-[0.85rem] font-medium", tone.text)}>
            {diagnosis.title}
          </div>
          <p className="mt-1 text-[0.8rem] leading-snug text-muted">
            {diagnosis.detail}
          </p>

          {diagnosis.remedy && (
            <p className="mt-1.5 text-[0.8rem] leading-snug">
              <span className="font-medium">Remède — </span>
              <span className="text-muted">{diagnosis.remedy}</span>
            </p>
          )}
        </div>

        {/* Le sélecteur de nature n'est pas un détail de réglage : c'est lui qui décide
            si l'autocorrélation est un phénomène attendu ou une dérive à corriger. */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-[0.65rem] tracking-wide text-faint uppercase">
            Cette série mesure
          </span>
          <Select
            value={diagnosis.nature}
            onChange={(e) => onNatureChange(code, e.target.value)}
          >
            {Object.entries(NATURE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <span className="tnum text-[0.65rem] text-faint">
            ρ = {diagnosis.rho.toFixed(2)}
            {!calm &&
              ` · limites ×${diagnosis.narrowing.toFixed(1)} trop étroites`}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Pastille compacte pour la vue d'ensemble : signale qu'une ligne rouge peut n'être
 *  qu'un artefact de la carte, sans obliger à ouvrir le détail. */
export function DiagnosisDot({
  diagnosis,
}: {
  diagnosis: SeriesDiagnosis | null;
}) {
  if (!diagnosis || diagnosis.severity === "none") return null;
  return (
    <span
      title={`${diagnosis.title} — ρ = ${diagnosis.rho.toFixed(2)}`}
      className={cn(
        "ml-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle",
        diagnosis.is_misleading ? "bg-warn" : "bg-faint",
      )}
    />
  );
}
